import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Search, 
  Filter, 
  Grid3X3, 
  List, 
  Heart,
  Download,
  Star,
  Printer,
  Eye,
  Sparkles,
  TrendingUp,
  Clock,
  Tag
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

interface Model {
  id: string
  name: string
  creator: string
  price: number | 'free'
  downloads: number
  likes: number
  rating: number
  category: string
  thumbnail: string | null
  aiGenerated: boolean
  printTime: string
  tags: string[]
}

const models: Model[] = [
  { id: '1', name: 'Modular Phone Stand', creator: 'DesignPro', price: 4.99, downloads: 12500, likes: 890, rating: 4.8, category: 'Accessories', thumbnail: null, aiGenerated: false, printTime: '2h 30m', tags: ['phone', 'stand', 'modular'] },
  { id: '2', name: 'Geometric Planter', creator: 'PlantLover3D', price: 'free', downloads: 8900, likes: 654, rating: 4.6, category: 'Home', thumbnail: null, aiGenerated: false, printTime: '4h 15m', tags: ['planter', 'geometric', 'decor'] },
  { id: '3', name: 'AI Cable Manager', creator: '3DMakerlyAI', price: 2.99, downloads: 5600, likes: 432, rating: 4.9, category: 'Office', thumbnail: null, aiGenerated: true, printTime: '1h 45m', tags: ['cable', 'organizer', 'desk'] },
  { id: '4', name: 'Mechanical Keyboard Case', creator: 'KeyboardNerd', price: 12.99, downloads: 3400, likes: 567, rating: 4.7, category: 'Electronics', thumbnail: null, aiGenerated: false, printTime: '8h 30m', tags: ['keyboard', 'case', 'mechanical'] },
  { id: '5', name: 'Desk Lamp Shade', creator: 'LightDesigns', price: 'free', downloads: 7800, likes: 789, rating: 4.5, category: 'Lighting', thumbnail: null, aiGenerated: false, printTime: '3h 20m', tags: ['lamp', 'shade', 'lighting'] },
  { id: '6', name: 'AI Vase Collection', creator: '3DMakerlyAI', price: 6.99, downloads: 2100, likes: 345, rating: 4.8, category: 'Home', thumbnail: null, aiGenerated: true, printTime: '5h 00m', tags: ['vase', 'decor', 'artistic'] },
]

const categories = ['All', 'Accessories', 'Home', 'Office', 'Electronics', 'Lighting', 'Art', 'Functional']

function ModelCard({ model }: { model: Model }) {
  const [isLiked, setIsLiked] = useState(false)

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="group"
    >
      <Card hover className="overflow-hidden">
        <div className="aspect-square bg-surface-800 relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 bg-surface-700 rounded-xl flex items-center justify-center">
              <Printer className="w-12 h-12 text-surface-500" />
            </div>
          </div>
          
          {model.aiGenerated && (
            <div className="absolute top-3 left-3">
              <span className="px-2 py-1 bg-ai-500/20 backdrop-blur-sm text-ai-400 text-xs font-medium rounded-full flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> AI Generated
              </span>
            </div>
          )}
          
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setIsLiked(!isLiked)}
              className={cn(
                'p-2 rounded-full backdrop-blur-sm transition-colors',
                isLiked ? 'bg-error-500/20 text-error-400' : 'bg-surface-900/60 text-white hover:bg-surface-900/80'
              )}
            >
              <Heart className={cn('w-4 h-4', isLiked && 'fill-current')} />
            </button>
          </div>

          <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-surface-900/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex gap-2">
              <Button variant="primary" size="sm" className="flex-1">
                <Eye className="w-4 h-4" /> Preview
              </Button>
              <Button variant="secondary" size="sm">
                <Download className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <h3 className="font-semibold text-white group-hover:text-primary-400 transition-colors line-clamp-1">
                {model.name}
              </h3>
              <p className="text-sm text-surface-400">{model.creator}</p>
            </div>
            <div className="text-right flex-shrink-0">
              {model.price === 'free' ? (
                <span className="text-success-400 font-semibold">Free</span>
              ) : (
                <span className="text-white font-semibold">${model.price}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs text-surface-400">
            <span className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-warning-400 fill-warning-400" />
              {model.rating}
            </span>
            <span className="flex items-center gap-1">
              <Download className="w-3.5 h-3.5" />
              {model.downloads.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {model.printTime}
            </span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

export default function Marketplace() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState<'popular' | 'new' | 'price'>('popular')

  const filteredModels = models.filter(model => {
    const matchesSearch = model.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          model.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    const matchesCategory = selectedCategory === 'All' || model.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  return (
    <div className="h-full overflow-y-auto">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary-900/50 via-surface-900 to-ai-900/30 border-b border-surface-800">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-3">
              <span className="text-gradient">MakerHub</span> Marketplace
            </h1>
            <p className="text-surface-300 text-lg max-w-2xl mx-auto">
              Discover, download, and sell 3D printable models. AI-powered recommendations and instant print-on-demand.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search models, creators, or describe what you need..."
                className="w-full h-14 pl-12 pr-32 bg-surface-800/80 backdrop-blur border border-surface-700 rounded-2xl text-white placeholder:text-surface-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <Button variant="ai" size="sm">
                  <Sparkles className="w-4 h-4" /> AI Search
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters Row */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
                  selectedCategory === category
                    ? 'bg-primary-600 text-white'
                    : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                )}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'popular' | 'new' | 'price')}
              className="h-10 px-3 bg-surface-800 border border-surface-700 rounded-lg text-sm text-surface-200 focus:outline-none focus:border-primary-500"
            >
              <option value="popular">Most Popular</option>
              <option value="new">Newest</option>
              <option value="price">Price: Low to High</option>
            </select>

            <div className="flex bg-surface-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  'p-2 rounded-md transition-colors',
                  viewMode === 'grid' ? 'bg-surface-700 text-white' : 'text-surface-400 hover:text-white'
                )}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  'p-2 rounded-md transition-colors',
                  viewMode === 'list' ? 'bg-surface-700 text-white' : 'text-surface-400 hover:text-white'
                )}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <Button variant="secondary" size="sm">
              <Filter className="w-4 h-4" /> Filters
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-gradient-to-r from-primary-900/30 to-surface-900 border-primary-800/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Trending Today</p>
                <p className="text-white font-semibold">2,847 downloads</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-ai-900/30 to-surface-900 border-ai-800/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-ai-500/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-ai-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">AI Generated</p>
                <p className="text-white font-semibold">156 new models</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-success-900/30 to-surface-900 border-success-800/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-success-500/20 flex items-center justify-center">
                <Tag className="w-5 h-5 text-success-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Free Models</p>
                <p className="text-white font-semibold">45,000+ available</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-surface-400">
            Showing <span className="text-white font-medium">{filteredModels.length}</span> results
          </p>
        </div>

        {/* Models Grid */}
        <div className={cn(
          'grid gap-6',
          viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'
        )}>
          {filteredModels.map((model) => (
            <ModelCard key={model.id} model={model} />
          ))}
        </div>

        {/* Load More */}
        <div className="text-center mt-12">
          <Button variant="secondary" size="lg">
            Load More Models
          </Button>
        </div>
      </div>
    </div>
  )
}
